import type { EntityStatus } from '@ms/yammer-data-support';
import type { AutocompleteUserResultFilterType } from '@ms/yammer-graphql';
import { AutocompleteOfficeGroupResultFilter } from '@ms/yammer-graphql';
import type { Reducer } from 'redux';

import type {
  TypeaheadAction,
  TypeaheadFulfilledAction,
  TypeaheadTopicResetAction,
} from '../../domains/typeahead/actions.types';
import { getId as getTypeaheadId } from '../../domains/typeahead/getId';

import type { Typeahead, TypeaheadResult, TypeaheadUniqueResultsOptions, Typeaheadable } from './types';

export type { TypeaheadAction };

const termsForName = (fullName: string) => {
  const names = fullName.split(' ');

  return names.reduce((terms, name, i) => {
    if (!name) {
      return terms;
    }

    return [...terms, `${name} ${names.slice(i + 1).join(' ')}`];
  }, []);
};

const uniqueTerms = (term: string, index: number, self: string[]) => self.indexOf(term) === index;
const uniqueOfficeGroupResultFilters = (
  officeGroupResultFilter: AutocompleteOfficeGroupResultFilter,
  index: number,
  self: AutocompleteOfficeGroupResultFilter[]
) => self.indexOf(officeGroupResultFilter) === index;

type GetOfficeGroupResultFilters = (
  resultOfficeGroupResultFilters?: AutocompleteOfficeGroupResultFilter[],
  otherResultOfficeGroupResultFilters?: AutocompleteOfficeGroupResultFilter[]
) => AutocompleteOfficeGroupResultFilter[] | undefined;
export const getOfficeGroupResultFilters: GetOfficeGroupResultFilters = (
  resultOfficeGroupResultFilters,
  otherResultOfficeGroupResultFilters
) => {
  if (!resultOfficeGroupResultFilters) {
    return otherResultOfficeGroupResultFilters ?? undefined;
  }

  if (resultOfficeGroupResultFilters && !otherResultOfficeGroupResultFilters) {
    return resultOfficeGroupResultFilters;
  }

  return [
    ...(resultOfficeGroupResultFilters as AutocompleteOfficeGroupResultFilter[]),
    ...(otherResultOfficeGroupResultFilters as AutocompleteOfficeGroupResultFilter[]),
  ].filter(uniqueOfficeGroupResultFilters);
};

const mergeResult = <T extends Typeaheadable>(
  result: TypeaheadResult<T>,
  otherResult: TypeaheadResult<T>
): TypeaheadResult<T> => {
  if (!otherResult) {
    return result;
  }
  const terms = [...result.terms, ...otherResult.terms].filter(uniqueTerms);
  const officeGroupResultFilters = getOfficeGroupResultFilters(
    result.officeGroupResultFilters,
    otherResult.officeGroupResultFilters
  );

  return {
    ...result,
    terms,
    groupResultFilter: result.groupResultFilter || otherResult.groupResultFilter,
    userResultFilters: result.userResultFilters || otherResult.userResultFilters,
    topicResultFilter: result.topicResultFilter || otherResult.topicResultFilter,
    officeGroupResultFilters,
  };
};

const mergeResults = <T extends Typeaheadable>(
  memo: Record<string, TypeaheadResult<T>>,
  result: TypeaheadResult<T>
) => ({
  ...memo,
  [result.id]: mergeResult<T>(result, memo[result.id]),
});

const uniqueResults = <T extends Typeaheadable>({
  search,
  existingResults,
  newResults,
  entities,
  groupResultFilter,
  userResultFilters,
  groupIds,
  topicResultFilter,
  officeGroupResultFilter,
  userNetworkLeaderEntities,
  contextGroupId,
}: TypeaheadUniqueResultsOptions<T>) => {
  const bothResults = [
    ...existingResults,
    ...newResults.map((id) => ({
      id,
      terms: [search.toLowerCase(), ...termsForName(entities[id].name.toLowerCase())],
      groupResultFilter,
      userResultFilters,
      groupIds,
      topicResultFilter,
      officeGroupResultFilters: officeGroupResultFilter ? [officeGroupResultFilter] : undefined,
      isLeader: userNetworkLeaderEntities?.[id]?.isLeader,
      contextGroupId,
    })),
  ];
  const mergedResultsDictionary = bothResults.reduce<Record<string, TypeaheadResult<T>>>(mergeResults, {});

  return Object.keys(mergedResultsDictionary).map((id) => mergedResultsDictionary[id]);
};

const initialState: Typeahead = {
  requests: {
    users: {},
    groups: {},
    topics: {},
    officeGroups: {},
    campaignReferences: {},
  },
  results: {
    users: [],
    groups: [],
    topics: [],
    officeGroups: [],
    campaignReferences: [],
  },
};

const stateWithUpdatedResults = (state: Typeahead, payload: TypeaheadFulfilledAction['payload']): Typeahead => {
  const {
    search,
    options: {
      includeUsers,
      includeGroups,
      includeTopics,
      includeCampaigns,
      includeOfficeGroups,
      groupIds,
      topicResultFilter,
      officeGroupResultFilter,
      userResultFilters,
      groupResultFilter,
      contextGroupId,
    },
    entities,
    result,
  } = payload;

  const {
    results: { users, topics, groups, officeGroups, campaignReferences },
  } = state;

  return {
    ...state,
    results: {
      ...state.results,
      users: includeUsers
        ? uniqueResults({
            search,
            existingResults: users,
            newResults: result.users,
            entities: entities.users,
            userResultFilters,
            groupIds,
            userNetworkLeaderEntities: entities.userNetworkLeaders,
          })
        : users,
      topics: includeTopics
        ? uniqueResults({
            search,
            existingResults: topics,
            newResults: result.topics,
            entities: entities.topics,
            topicResultFilter,
          })
        : topics,
      groups: includeGroups
        ? uniqueResults({
            search,
            existingResults: [],
            newResults: result.groups,
            entities: entities.groups,
            groupResultFilter,
          })
        : groups,
      officeGroups: includeOfficeGroups
        ? uniqueResults({
            search,
            existingResults: officeGroups,
            newResults: result.officeGroups,
            entities: entities.officeGroups,
            officeGroupResultFilter: officeGroupResultFilter || AutocompleteOfficeGroupResultFilter.EXCLUDE_M365_GROUPS,
          })
        : officeGroups,
      campaignReferences: includeCampaigns
        ? uniqueResults({
            search,
            existingResults: campaignReferences,
            newResults: result.campaignReferences,
            entities: entities.campaignReferences,
            contextGroupId,
          })
        : campaignReferences,
    },
  };
};

const stateWithTopicResultsReset = (state: Typeahead): Typeahead => ({
  ...state,
  requests: {
    ...state.requests,
    topics: {},
  },
  results: {
    ...state.results,
    topics: [],
  },
});

const stateWithUpdatedRequests = (
  state: Typeahead,
  payload: TypeaheadAction['payload'],
  status: EntityStatus
): Typeahead => {
  const {
    search,
    options: {
      includeUsers,
      includeGroups,
      includeTopics,
      includeOfficeGroups,
      includeCampaigns,
      userResultFilters,
      groupResultFilter,
      topicResultFilter,
      groupIds,
      officeGroupResultFilter,
      contextGroupId,
      onBehalfOfUserId,
    },
  } = payload;

  const {
    requests: { users, topics, groups, officeGroups, campaignReferences },
  } = state;

  return {
    ...state,
    requests: {
      users: includeUsers
        ? {
            ...users,
            [getTypeaheadId({
              search,
              type: 'users',
              groupIds,
              userResultFilters,
            })]: status,
          }
        : users,
      topics: includeTopics
        ? { ...topics, [getTypeaheadId({ search, type: 'topics', topicResultFilter })]: status }
        : topics,
      groups: includeGroups
        ? {
            [getTypeaheadId({
              search,
              type: 'groups',
              groupResultFilter,
              onBehalfOfUserId,
            })]: status,
          }
        : groups,
      officeGroups: includeOfficeGroups
        ? { ...officeGroups, [getTypeaheadId({ search, type: 'officeGroups', officeGroupResultFilter })]: status }
        : officeGroups,
      campaignReferences: includeCampaigns
        ? { ...campaignReferences, [getTypeaheadId({ search, type: 'campaignReferences', contextGroupId })]: status }
        : campaignReferences,
    },
  };
};

let previousContextThreadId: string | undefined;
let previousUserResultFilters: AutocompleteUserResultFilterType[] | undefined;

// As a temporary solution to that issue https://dev.azure.com/yammer/engineering/_workitems/edit/265529/
// This function is temporary and will be removed once the longer term solution regarding the issue is implemented
const stateWithProcessedUsers = (state: Typeahead, payload: TypeaheadAction['payload']): Typeahead => {
  const {
    options: { includeUsers, contextThreadId, userResultFilters },
  } = payload;

  const {
    requests: { users: requestsUsers },
    results: { users: resultsUsers },
  } = state;

  const shouldClearUserState =
    includeUsers && (contextThreadId !== previousContextThreadId || userResultFilters !== previousUserResultFilters);

  if (shouldClearUserState) {
    previousContextThreadId = contextThreadId;
    previousUserResultFilters = userResultFilters;
  }

  return {
    ...state,
    results: {
      ...state.results,
      users: shouldClearUserState ? [] : resultsUsers,
    },
    requests: {
      ...state.requests,
      users: shouldClearUserState ? {} : requestsUsers,
    },
  };
};

const reducer: Reducer<Typeahead, TypeaheadAction | TypeaheadTopicResetAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'TYPEAHEAD.LOAD_PENDING': {
      return stateWithProcessedUsers(stateWithUpdatedRequests(state, action.payload, 'pending'), action.payload);
    }
    case 'TYPEAHEAD.LOAD_REJECTED': {
      return stateWithUpdatedRequests(state, action.payload, 'rejected');
    }
    case 'TYPEAHEAD.LOAD_FULFILLED': {
      return stateWithUpdatedResults(stateWithUpdatedRequests(state, action.payload, 'fulfilled'), action.payload);
    }
    case 'TYPEAHEAD.TOPIC_RESET': {
      return stateWithTopicResultsReset(stateWithTopicResultsReset(state));
    }
    default:
      return state;
  }
};

export default reducer;
